import classNames from 'classnames';
import React from 'react';

import { Showtime } from 'types/graphql-api.generated';

import MidnightBadge from 'common/components/MidnightBadge';
import ShowtimesHour from 'common/components/Showtimes/ShowtimesHour';
import { TheaterReservationTracking } from 'common/constants/trackingEventsNames';
import formatDate, { DATE_FORMATS } from 'common/tools/date/format';
import {
  dateProcessingFromAString,
  formatDayString
} from 'common/tools/date/showtime';
import getMidnightNightLabel from 'common/tools/showtime/getMidnightNightLabel';
import getTicketingUrl from 'common/tools/showtime/getTicketingUrl';
import {
  DayBlockPosition,
  groupShowtimesByDayBlocks
} from 'common/tools/showtime/groupShowtimesByDayBlocks';

export type OpenModalShowtimes = (
  showtimeId: number,
  fallback: (() => void) | null,
  tracking: TheaterReservationTracking
) => void;

type PartialShowtime = Pick<
  Showtime,
  | '__typename'
  | 'comfort'
  | 'data'
  | 'diffusionVersion'
  | 'experience'
  | 'internalId'
  | 'picture'
  | 'projection'
  | 'service'
  | 'sound'
  | 'startsAt'
  | 'tags'
  | 'timeBeforeStart'
>;

type ShowtimesVersionProps = {
  // the day the user is currently viewing ('YYYY-MM-DD'), used for the standard block's header
  currentDay?: string;
  hideDate?: boolean;
  openModal?: OpenModalShowtimes;
  // only render the blocks at this position of the day: the holder lists every version's blocks
  // position by position, to keep the whole list chronological. All the blocks when omitted.
  position?: DayBlockPosition;
  showtimesHours: Array<PartialShowtime>;
  showtimesLabel: string;
  tracking: Omit<TheaterReservationTracking, 'showtime'>;
  versionHint?: React.ReactNode;
};

const renderShowtimesHours = (
  showtimesHours: Array<PartialShowtime>,
  openModal: OpenModalShowtimes | undefined,
  tracking: Omit<TheaterReservationTracking, 'showtime'>
) => (
  <>
    {showtimesHours.map((showtime, index) => {
      const startsAtData = dateProcessingFromAString(showtime.startsAt);

      // use index for a key is a bad practice, but showtime hour is too messy and always trigger some key error
      const key = `${showtime.internalId}_${index}_${showtime.startsAt}`;

      const isPast = startsAtData.isPast;

      const ticketingUrl = getTicketingUrl(showtime?.data?.ticketing);
      const className = classNames(
        { past: isPast },
        { bookable: ticketingUrl && !isPast }
      );

      return (
        <ShowtimesHour
          ticketingUrl={ticketingUrl}
          className={className}
          hour={startsAtData.hour}
          experiences={showtime.tags}
          key={key}
          openModal={openModal?.bind(null, showtime.internalId)}
          tracking={{
            ...tracking,
            showtime
          }}
        />
      );
    })}
  </>
);

const ShowtimesVersion = ({
  currentDay,
  hideDate,
  openModal,
  position,
  showtimesHours,
  showtimesLabel,
  tracking,
  versionHint
}: ShowtimesVersionProps) => {
  if (!showtimesHours.length) {
    return null;
  }

  const isToday = !!currentDay && currentDay === formatDayString(new Date());

  // the same midnight showtime is shown both under J-1 (after the evening showtimes) and under J
  // (before them), a day can hold two nights: see groupShowtimesByDayBlocks, ALLOCINE-15181
  return (
    <>
      {groupShowtimesByDayBlocks(showtimesHours, currentDay)
        .filter(block => !position || block.position === position)
        .map(({ night, showtimes }) =>
          night ? (
            <div
              className="showtimes-version showtimes-version-midnight"
              key={formatDayString(night)}
            >
              <div className="text">
                <MidnightBadge iconOnly />
                {`${getMidnightNightLabel(night)} - ${showtimesLabel}`}
              </div>
              {versionHint}
              <div className="hours">
                {renderShowtimesHours(showtimes, openModal, tracking)}
              </div>
            </div>
          ) : (
            <div className="showtimes-version" key="standard">
              <div className="text">
                {hideDate || !currentDay || isToday
                  ? showtimesLabel
                  : `${formatDate(new Date(currentDay), {
                      timeZone: 'UTC',
                      ...DATE_FORMATS.LONG
                    })} - ${showtimesLabel}`}
              </div>
              {versionHint}
              <div className="hours">
                {renderShowtimesHours(showtimes, openModal, tracking)}
              </div>
            </div>
          )
        )}
    </>
  );
};

export default ShowtimesVersion;
